import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasSellerAccess } from '../utils/subscriptionUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { 
  Camera, Scan, DollarSign, Sparkles, Crown, 
  CheckCircle, ChevronRight, X, Check, Video, Upload, 
  Shield, Truck, BadgeCheck, Edit3, Package, RotateCcw
} from 'lucide-react';

// ==================== MAIN COMPONENT ====================

function AICardScannerPage() {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const frontUploadRef = useRef(null);
  const backUploadRef = useRef(null);
  
  // Dual image state
  const [frontImage, setFrontImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState('');
  const [backImage, setBackImage] = useState(null);
  const [backPreview, setBackPreview] = useState('');
  
  // Scan state
  const [scanning, setScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  
  // Editable card data fields
  const [cardData, setCardData] = useState({
    player: '',
    year: '',
    set: '',
    parallel: '',
    cardNumber: '',
    grader: '',
    grade: '',
    isNumbered: false,
    printRun: ''
  });
  
  // Pricing state
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [listingPrice, setListingPrice] = useState('');
  
  // Shipping
  const [shippingEnabled, setShippingEnabled] = useState(true);
  
  // Listing state
  const [listing, setListing] = useState(false);
  
  // Current step
  const [currentStep, setCurrentStep] = useState(1); // 1: Scan, 2: Price, 3: Review

  // Grader options
  const graderOptions = ['PSA', 'BGS', 'SGC', 'CGC', 'CSG', 'HGA', 'Raw'];

  // Use centralized subscription check - prioritizes Stripe status
  const hasSub = isAuthenticated && hasSellerAccess(user);

  // ==================== IMAGE HANDLING ====================
  
  const handleFrontImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setFrontImage(file);
      setFrontPreview(URL.createObjectURL(file));
      resetScanState();
    }
  };

  const handleBackImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setBackImage(file);
      setBackPreview(URL.createObjectURL(file));
    }
  };

  const clearFrontImage = () => {
    setFrontImage(null);
    setFrontPreview('');
    resetScanState();
  };

  const clearBackImage = () => {
    setBackImage(null);
    setBackPreview('');
  };

  const resetScanState = () => {
    setScanComplete(false);
    setScanResult(null);
    setPriceData(null);
    setListingPrice('');
    setCurrentStep(1);
    setCardData({
      player: '',
      year: '',
      set: '',
      parallel: '',
      cardNumber: '',
      grader: '',
      grade: '',
      isNumbered: false,
      printRun: ''
    });
  };

  // ==================== AI SCAN ====================
  
  const doScan = async () => {
    if (!frontImage) {
      toast.error('Please capture or upload a card front image');
      return;
    }
    
    setScanning(true);
    
    try {
      const fd = new FormData();
      fd.append('front_image', frontImage);
      if (backImage) {
        fd.append('back_image', backImage);
      }
      
      const res = await api().post('/cards/ai-scan', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const result = res.data;
      setScanResult(result);
      setScanComplete(true);
      
      // Extract and populate editable fields
      const meta = result.metadata || {};
      const condition = result.condition_report || {};
      
      const graderVal = condition.is_graded ? (condition.grader || 'PSA') : 'Raw';
      const gradeVal = condition.grade_value ? condition.grade_value.toString() : '';
      
      setCardData({
        player: meta.name || '',
        year: meta.year ? meta.year.toString() : '',
        set: meta.set || '',
        parallel: meta.parallel || '',
        cardNumber: meta.card_number || '',
        grader: graderVal,
        grade: gradeVal,
        isNumbered: !!meta.parallel && (meta.parallel.toLowerCase().includes('/') || meta.parallel.toLowerCase().includes('numbered')),
        printRun: ''
      });
      
      toast.success('Card scanned! Review and edit the details below.');
      
    } catch (e) {
      console.error('Scan error:', e);
      toast.error('Scan failed. Please try again or enter details manually.');
      setScanComplete(true);
    }
    setScanning(false);
  };

  // ==================== GET PRICE ====================
  
  const getPrice = async () => {
    if (!cardData.player || !cardData.year || !cardData.set) {
      toast.error('Please fill in Player, Year, and Set to get pricing');
      return;
    }
    
    setLoadingPrice(true);
    
    try {
      const payload = {
        player_name: cardData.player,
        sport: scanResult?.metadata?.sub_category || 'Sports',
        set_name: cardData.set,
        year: parseInt(cardData.year) || new Date().getFullYear(),
        card_number: cardData.cardNumber || null,
        parallel: cardData.parallel || null,
        grade_company: cardData.grader !== 'Raw' ? cardData.grader : null,
        grade_value: cardData.grade || null
      };
      
      const res = await api().post('/cards/market-value', payload);
      
      if (res.data) {
        setPriceData(res.data);
        setListingPrice(res.data.estimated_value?.toFixed(2) || '');
        toast.success('Market value retrieved!');
        setCurrentStep(2);
      }
    } catch (e) {
      console.error('Price error:', e);
      toast.error('Could not retrieve market value. Set your own price.');
      setCurrentStep(2);
    }
    setLoadingPrice(false);
  };

  // ==================== LISTING ====================
  
  const doList = async () => {
    if (!hasSub) {
      toast.error('Subscription required to list cards');
      return;
    }
    
    if (!listingPrice || parseFloat(listingPrice) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setListing(true);
    try {
      // Upload front image
      let frontUrl = '';
      if (frontImage) {
        const uploadFd = new FormData();
        uploadFd.append('file', frontImage);
        const uploadRes = await api().post('/upload/listing-image', uploadFd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        frontUrl = uploadRes.data.image_url;
      }
      
      // Upload back image
      let backUrl = '';
      if (backImage) {
        const uploadFd = new FormData();
        uploadFd.append('file', backImage);
        const uploadRes = await api().post('/upload/listing-image', uploadFd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        backUrl = uploadRes.data.image_url;
      }
      
      const images = [];
      if (frontUrl) images.push(frontUrl);
      if (backUrl) images.push(backUrl);
      
      const price = parseFloat(listingPrice);
      const isGraded = cardData.grader && cardData.grader !== 'Raw';
      
      const description = [
        cardData.year,
        cardData.set,
        cardData.player,
        cardData.parallel,
        isGraded ? `${cardData.grader} ${cardData.grade}` : 'Raw',
        cardData.cardNumber ? `#${cardData.cardNumber}` : ''
      ].filter(Boolean).join(' ').trim();
      
      await api().post('/cards', {
        player_name: cardData.player,
        set_name: cardData.set || 'Unknown Set',
        year: parseInt(cardData.year) || new Date().getFullYear(),
        grade_company: isGraded ? cardData.grader.toUpperCase() : 'Raw',
        grade_value: cardData.grade || null,
        price: price,
        description: description,
        image_url: frontUrl,
        images: images,
        sport: scanResult?.metadata?.sub_category || scanResult?.metadata?.category || 'Other',
        item_specifics: {
          verified_by_ai: !!scanResult,
          scan_confidence: scanResult?.scan_confidence,
          parallel_variety: cardData.parallel,
          card_number: cardData.cardNumber,
          is_serial_numbered: cardData.isNumbered,
          total_print_run: cardData.printRun ? parseInt(cardData.printRun) : null,
          has_back_image: !!backUrl
        }
      });
      
      toast.success('Card listed successfully!');
      navigate('/marketplace');
    } catch (e) {
      console.error('List error:', e);
      toast.error('Failed to list card: ' + (e.response?.data?.detail || e.message));
    }
    setListing(false);
  };

  // Calculate platform savings
  const platformSavings = listingPrice ? (parseFloat(listingPrice) * 0.13).toFixed(2) : '0.00';

  // ==================== AUTH CHECK ====================
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <Navbar />
        <div className="pt-24 px-4 max-w-md mx-auto text-center">
          <Scan className="w-16 h-16 text-[#00BFFF] mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-4">Scan & List</h1>
          <p className="text-zinc-400 mb-6">Sign in to scan and list your cards</p>
          <Link to="/login"><Button className="mr-2">Login</Button></Link>
          <Link to="/register"><Button variant="outline">Register</Button></Link>
        </div>
      </div>
    );
  }

  // ==================== RENDER ====================
  
  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <div className="pt-24 px-4 max-w-2xl mx-auto pb-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-6 h-6 text-[#00BFFF]" />
            <h1 className="text-2xl font-bold text-white">Scan & List</h1>
          </div>
          <p className="text-zinc-400 text-sm">AI-powered card identification with manual override</p>
        </div>

        {/* Step Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              currentStep > 1 ? 'bg-emerald-500 text-black' :
              currentStep === 1 ? 'bg-[#00BFFF] text-black' : 'bg-zinc-800 text-zinc-500'
            }`}>
              {currentStep > 1 ? <Check className="w-4 h-4" /> : 1}
            </div>
            <span className={`ml-2 text-sm ${currentStep >= 1 ? 'text-white' : 'text-zinc-600'}`}>Scan</span>
            <ChevronRight className="w-4 h-4 text-zinc-700 mx-3" />
          </div>
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              currentStep > 2 ? 'bg-emerald-500 text-black' :
              currentStep === 2 ? 'bg-[#00BFFF] text-black' : 'bg-zinc-800 text-zinc-500'
            }`}>
              {currentStep > 2 ? <Check className="w-4 h-4" /> : 2}
            </div>
            <span className={`ml-2 text-sm ${currentStep >= 2 ? 'text-white' : 'text-zinc-600'}`}>Price</span>
            <ChevronRight className="w-4 h-4 text-zinc-700 mx-3" />
          </div>
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              currentStep === 3 ? 'bg-[#00BFFF] text-black' : 'bg-zinc-800 text-zinc-500'
            }`}>
              3
            </div>
            <span className={`ml-2 text-sm ${currentStep >= 3 ? 'text-white' : 'text-zinc-600'}`}>List</span>
          </div>
        </div>

        {/* ==================== STEP 1: SCAN ==================== */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Camera className="w-5 h-5 text-[#00BFFF]" />
                Card Images
              </h2>
              
              {/* Dual Image Upload Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Front Image */}
                <div>
                  <Label className="text-zinc-400 text-sm mb-2 block">Card Front *</Label>
                  <div className={`aspect-[3/4] bg-zinc-800 border-2 border-dashed rounded-xl flex items-center justify-center overflow-hidden transition-all ${
                    frontPreview ? 'border-emerald-500/50' : 'border-zinc-700'
                  }`}>
                    {frontPreview ? (
                      <div className="relative w-full h-full">
                        <img src={frontPreview} alt="Card Front" className="w-full h-full object-contain" />
                        <button
                          onClick={clearFrontImage}
                          className="absolute top-2 right-2 p-1.5 bg-black/70 rounded-full hover:bg-red-600 transition-colors"
                          data-testid="clear-front-image"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                        {scanComplete && (
                          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-emerald-500 text-black text-xs font-bold rounded">
                            <Shield className="w-3 h-3" />
                            Scanned
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <Camera className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                        <p className="text-zinc-500 text-xs mb-3">Front of card</p>
                        <div className="flex flex-col gap-2">
                          <Button 
                            size="sm"
                            onClick={() => frontInputRef.current && frontInputRef.current.click()}
                            className="bg-[#00BFFF] text-black text-xs"
                            data-testid="capture-front-btn"
                          >
                            <Video className="w-3 h-3 mr-1" />
                            Camera
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => frontUploadRef.current && frontUploadRef.current.click()}
                            className="text-xs"
                            data-testid="upload-front-btn"
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            Upload
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <input ref={frontInputRef} type="file" accept="image/*" capture="environment" onChange={handleFrontImage} className="hidden" />
                  <input ref={frontUploadRef} type="file" accept="image/*" onChange={handleFrontImage} className="hidden" />
                </div>

                {/* Back Image */}
                <div>
                  <Label className="text-zinc-400 text-sm mb-2 block">Card Back (Optional)</Label>
                  <div className={`aspect-[3/4] bg-zinc-800 border-2 border-dashed rounded-xl flex items-center justify-center overflow-hidden transition-all ${
                    backPreview ? 'border-blue-500/50' : 'border-zinc-700'
                  }`}>
                    {backPreview ? (
                      <div className="relative w-full h-full">
                        <img src={backPreview} alt="Card Back" className="w-full h-full object-contain" />
                        <button
                          onClick={clearBackImage}
                          className="absolute top-2 right-2 p-1.5 bg-black/70 rounded-full hover:bg-red-600 transition-colors"
                          data-testid="clear-back-image"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                        <div className="absolute top-2 left-2 px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded">
                          Back
                        </div>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <RotateCcw className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                        <p className="text-zinc-500 text-xs mb-3">Back of card</p>
                        <div className="flex flex-col gap-2">
                          <Button 
                            size="sm"
                            onClick={() => backInputRef.current && backInputRef.current.click()}
                            className="bg-blue-600 text-white text-xs"
                            data-testid="capture-back-btn"
                          >
                            <Video className="w-3 h-3 mr-1" />
                            Camera
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => backUploadRef.current && backUploadRef.current.click()}
                            className="text-xs"
                            data-testid="upload-back-btn"
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            Upload
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <input ref={backInputRef} type="file" accept="image/*" capture="environment" onChange={handleBackImage} className="hidden" />
                  <input ref={backUploadRef} type="file" accept="image/*" onChange={handleBackImage} className="hidden" />
                </div>
              </div>

              {/* Scan Button */}
              {frontPreview && !scanComplete && (
                <Button 
                  onClick={doScan}
                  disabled={scanning}
                  className="w-full h-12 bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black font-bold"
                  data-testid="scan-btn"
                >
                  {scanning ? (
                    <span className="flex items-center">
                      <LoadingSpinner size="sm" className="mr-2" />
                      Scanning Card...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Scan className="w-5 h-5 mr-2" />
                      Scan & Identify
                    </span>
                  )}
                </Button>
              )}

              {/* Editable Card Details */}
              {scanComplete && (
                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
                      <Edit3 className="w-4 h-4" />
                      Card Details (Editable)
                    </h3>
                    {scanResult && (
                      <span className="text-xs text-emerald-400 flex items-center gap-1">
                        <BadgeCheck className="w-3 h-3" />
                        AI Confidence: {Math.round((scanResult.scan_confidence || 0.5) * 100)}%
                      </span>
                    )}
                  </div>
                  
                  {/* Row 1: Player & Year */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Player / Card Name *</Label>
                      <Input
                        value={cardData.player}
                        onChange={(e) => setCardData({ ...cardData, player: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="LeBron James"
                        data-testid="input-player"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Year *</Label>
                      <Input
                        type="number"
                        value={cardData.year}
                        onChange={(e) => setCardData({ ...cardData, year: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="2023"
                        data-testid="input-year"
                      />
                    </div>
                  </div>
                  
                  {/* Row 2: Set & Parallel */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Set / Brand *</Label>
                      <Input
                        value={cardData.set}
                        onChange={(e) => setCardData({ ...cardData, set: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="Prizm, Topps Chrome"
                        data-testid="input-set"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Parallel / Variant</Label>
                      <Input
                        value={cardData.parallel}
                        onChange={(e) => setCardData({ ...cardData, parallel: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="Silver Prizm, Refractor"
                        data-testid="input-parallel"
                      />
                    </div>
                  </div>
                  
                  {/* Row 3: Grader & Grade */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Grader</Label>
                      <Select 
                        value={cardData.grader} 
                        onValueChange={(v) => setCardData({ ...cardData, grader: v })}
                      >
                        <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="select-grader">
                          <SelectValue placeholder="Select grader" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#18181B] border-zinc-700">
                          <SelectItem value="PSA">PSA</SelectItem>
                          <SelectItem value="BGS">BGS</SelectItem>
                          <SelectItem value="SGC">SGC</SelectItem>
                          <SelectItem value="CGC">CGC</SelectItem>
                          <SelectItem value="CSG">CSG</SelectItem>
                          <SelectItem value="HGA">HGA</SelectItem>
                          <SelectItem value="Raw">Raw (Ungraded)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Grade</Label>
                      <Input
                        value={cardData.grade}
                        onChange={(e) => setCardData({ ...cardData, grade: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="10, 9.5, Auto 10"
                        disabled={cardData.grader === 'Raw'}
                        data-testid="input-grade"
                      />
                    </div>
                  </div>
                  
                  {/* Row 4: Card Number & Numbered Toggle */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Card Number</Label>
                      <Input
                        value={cardData.cardNumber}
                        onChange={(e) => setCardData({ ...cardData, cardNumber: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="#220"
                        data-testid="input-card-number"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs mb-2 block">Serial Numbered?</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <Switch
                          checked={cardData.isNumbered}
                          onCheckedChange={(checked) => setCardData({ ...cardData, isNumbered: checked })}
                          data-testid="toggle-numbered"
                        />
                        {cardData.isNumbered && (
                          <Input
                            value={cardData.printRun}
                            onChange={(e) => setCardData({ ...cardData, printRun: e.target.value })}
                            className="bg-zinc-800 border-zinc-700 w-20"
                            placeholder="/99"
                            data-testid="input-print-run"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Get Price Button */}
                  <Button 
                    onClick={getPrice}
                    disabled={loadingPrice || !cardData.player || !cardData.year || !cardData.set}
                    className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-black font-bold mt-4"
                    data-testid="get-price-btn"
                  >
                    {loadingPrice ? (
                      <span className="flex items-center">
                        <LoadingSpinner size="sm" className="mr-2" />
                        Fetching Market Value...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <DollarSign className="w-5 h-5 mr-2" />
                        Get Market Price
                      </span>
                    )}
                  </Button>
                  
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="w-full text-zinc-500 hover:text-white text-sm mt-2"
                  >
                    Skip pricing - set my own price
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== STEP 2: PRICE ==================== */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Card Preview */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="flex gap-4">
                <div className="flex gap-2">
                  {frontPreview && (
                    <img src={frontPreview} alt="Card Front" className="w-16 h-20 object-contain rounded-lg bg-zinc-800" />
                  )}
                  {backPreview && (
                    <img src={backPreview} alt="Card Back" className="w-16 h-20 object-contain rounded-lg bg-zinc-800" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <BadgeCheck className="w-4 h-4 text-emerald-400" />
                    <h3 className="text-white font-bold">{cardData.player}</h3>
                  </div>
                  <p className="text-zinc-400 text-sm">{cardData.year} {cardData.set}</p>
                  <p className="text-zinc-500 text-sm">
                    {cardData.grader !== 'Raw' ? `${cardData.grader} ${cardData.grade}` : 'Raw'}
                    {cardData.parallel && ` - ${cardData.parallel}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Market Value Display */}
            {priceData && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-zinc-400 mb-3">Market Value Estimate</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-zinc-800 rounded-lg">
                    <p className="text-xs text-zinc-500">Low</p>
                    <p className="text-white font-bold">${priceData.low_price?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">
                    <p className="text-xs text-emerald-400">Suggested</p>
                    <p className="text-emerald-400 font-bold text-lg">${priceData.estimated_value?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-800 rounded-lg">
                    <p className="text-xs text-zinc-500">High</p>
                    <p className="text-white font-bold">${priceData.high_price?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Pricing Input */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Set Your Price
              </h2>
              
              {/* Zero Commission Banner */}
              <div className="mb-6 p-4 bg-gradient-to-r from-emerald-500/20 to-[#00BFFF]/20 border border-emerald-500/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/30 flex items-center justify-center">
                    <span className="text-2xl font-black text-emerald-400">0%</span>
                  </div>
                  <div>
                    <p className="text-white font-bold">Zero Seller Fees</p>
                    <p className="text-zinc-400 text-sm">You keep 100% of your sale price</p>
                  </div>
                </div>
              </div>

              {/* Price Input */}
              <div className="mb-4">
                <Label className="text-white mb-2 block">Listing Price</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 text-2xl font-bold">$</span>
                  <Input 
                    type="number"
                    step="0.01"
                    min="0"
                    value={listingPrice}
                    onChange={(e) => setListingPrice(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 pl-10 h-16 text-3xl font-mono text-white"
                    placeholder="0.00"
                    data-testid="input-price"
                  />
                </div>
              </div>

              {/* Platform Comparison */}
              {listingPrice && parseFloat(listingPrice) > 0 && (
                <div className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                  <p className="text-zinc-300 text-sm">
                    On other platforms, you'd lose ~13% in fees. 
                    <span className="text-emerald-400 font-bold"> Here, you keep the full ${parseFloat(listingPrice).toFixed(2)}</span>.
                  </p>
                  <p className="text-zinc-500 text-xs mt-1">
                    That's <span className="text-emerald-400">${platformSavings}</span> more in your pocket!
                  </p>
                </div>
              )}

              {/* Shipping Toggle */}
              <div className="mt-6 p-4 bg-zinc-800 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-[#00BFFF]" />
                  <div>
                    <p className="text-white font-medium">Standard Tracked Shipping</p>
                    <p className="text-zinc-500 text-xs">USPS Ground Advantage</p>
                  </div>
                </div>
                <Switch 
                  checked={shippingEnabled} 
                  onCheckedChange={setShippingEnabled}
                  className="data-[state=checked]:bg-[#00BFFF]"
                />
              </div>

              {/* Continue Button */}
              <Button 
                onClick={() => setCurrentStep(3)}
                disabled={!listingPrice || parseFloat(listingPrice) <= 0}
                className="w-full mt-6 h-12 bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black font-bold"
                data-testid="continue-to-review-btn"
              >
                Review Listing
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>

              <button
                onClick={() => setCurrentStep(1)}
                className="w-full mt-3 text-zinc-500 hover:text-white text-sm"
              >
                Back to scan
              </button>
            </div>
          </div>
        )}

        {/* ==================== STEP 3: REVIEW & LIST ==================== */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              {/* Card Images */}
              <div className="flex gap-2 p-4 bg-zinc-800">
                {frontPreview && (
                  <img src={frontPreview} alt="Card Front" className="flex-1 max-w-[48%] h-48 object-contain rounded-lg" />
                )}
                {backPreview && (
                  <img src={backPreview} alt="Card Back" className="flex-1 max-w-[48%] h-48 object-contain rounded-lg" />
                )}
              </div>
              
              {/* Summary Details */}
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-zinc-800">
                  <span className="text-zinc-400">Card</span>
                  <div className="text-right">
                    <span className="text-white font-bold flex items-center gap-2 justify-end">
                      <BadgeCheck className="w-4 h-4 text-emerald-400" />
                      {cardData.player}
                    </span>
                    <span className="text-zinc-500 text-sm block">{cardData.year} {cardData.set}</span>
                    {cardData.parallel && <span className="text-zinc-600 text-xs block">{cardData.parallel}</span>}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Grade</span>
                  <span className="text-white">
                    {cardData.grader !== 'Raw' ? `${cardData.grader} ${cardData.grade}` : 'Raw (Ungraded)'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Your Price</span>
                  <span className="text-white font-mono text-xl">${parseFloat(listingPrice || 0).toFixed(2)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Seller Fee</span>
                  <span className="text-emerald-400 font-bold">$0.00</span>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                  <span className="text-white font-bold text-lg">Your Payout</span>
                  <span className="text-emerald-400 font-mono text-2xl font-bold">${parseFloat(listingPrice || 0).toFixed(2)}</span>
                </div>

                {/* Shipping Info */}
                <div className="flex items-center gap-2 pt-2 text-zinc-500 text-sm">
                  <Truck className="w-4 h-4" />
                  <span>Buyer pays shipping - USPS Ground Advantage</span>
                </div>
                
                {/* Images Info */}
                <div className="flex items-center gap-2 text-zinc-500 text-sm">
                  <Camera className="w-4 h-4" />
                  <span>{backPreview ? '2 images (front + back)' : '1 image (front only)'}</span>
                </div>
              </div>
            </div>

            {/* Confirm Button */}
            {hasSub ? (
              <Button 
                onClick={doList}
                disabled={listing}
                className="w-full h-14 text-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
                data-testid="confirm-list-btn"
              >
                {listing ? (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" className="mr-2" />
                    Listing...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <CheckCircle className="w-6 h-6 mr-2" />
                    Confirm & List Card
                  </span>
                )}
              </Button>
            ) : (
              <div className="text-center p-6 bg-zinc-900 border border-zinc-800 rounded-xl">
                <Crown className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <p className="text-white font-medium mb-2">Subscription Required</p>
                <p className="text-zinc-400 text-sm mb-4">Subscribe to list cards for sale</p>
                <Link to="/subscription">
                  <Button className="bg-amber-500 text-black hover:bg-amber-400">Subscribe - $20/mo</Button>
                </Link>
              </div>
            )}

            <button
              onClick={() => setCurrentStep(2)}
              className="w-full text-zinc-500 hover:text-white text-sm"
            >
              Back to pricing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AICardScannerPage;
